import { useEffect } from 'react'

interface KeyboardShortcutOptions {
  key: string
  ctrlOrCmd?: boolean
  shift?: boolean
  alt?: boolean
  preventDefault?: boolean
  excludeInputs?: boolean
}

/**
 * Register a global keyboard shortcut
 * @param options Keyboard shortcut configuration
 * @param callback Function to call when shortcut is pressed
 */
export function useGlobalKeyboardShortcut(
  options: KeyboardShortcutOptions,
  callback: (event: KeyboardEvent) => void
) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      // Check if we should exclude this event (e.g., user is typing in an input)
      if (options.excludeInputs && event.target) {
        const target = event.target as HTMLElement
        const isInput = ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
        const isContentEditable = target.isContentEditable

        // Allow shortcuts with modifiers even in inputs
        if (isInput || isContentEditable) {
          const hasModifier = options.ctrlOrCmd || options.shift || options.alt
          if (!hasModifier) {
            return
          }
        }
      }

      // Match key combination
      const keyMatches = event.key.toLowerCase() === options.key.toLowerCase()
      const ctrlCmdMatches = options.ctrlOrCmd ? event.ctrlKey || event.metaKey : true
      const shiftMatches = options.shift ? event.shiftKey : true
      const altMatches = options.alt ? event.altKey : true

      if (keyMatches && ctrlCmdMatches && shiftMatches && altMatches) {
        if (options.preventDefault !== false) {
          event.preventDefault()
        }
        callback(event)
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [
    options.key,
    options.ctrlOrCmd,
    options.shift,
    options.alt,
    options.preventDefault,
    options.excludeInputs,
    callback,
  ])
}
